import { TerritoryMarker } from './markers/TerritoryMarker'
import React from 'react'
import { Text, View } from 'react-native'
import Svg, { Image as SvgImage, Line, Path } from 'react-native-svg'
import { useMap } from '../../hooks/useMaps'
import { useTheme } from '../../hooks/useTheme'
import { getBundledMap } from '../../lib/bundledMaps'
import { MAP_BACKGROUND_IMAGES } from '../../lib/mapBackgrounds'
import { MapDecorations } from './MapDecorations'
import { assignRegionColors, buildRegionAdjacency } from './regionColors'

/** A static board preview: shared cached geometry, no game state or gestures. */
export const MapThumbnail = React.memo(function MapThumbnail({ mapId, width = 88, height = 64, loadFromNetwork = true }: {
  mapId: string | null; width?: number; height?: number; loadFromNetwork?: boolean
}) {
  const { data } = useMap(mapId ?? undefined, { enabled: loadFromNetwork })
  const map = data ?? getBundledMap(mapId ?? undefined)
  const { theme } = useTheme()
  const dark = theme === 'dark'
  const edge = dark ? '#929d70' : '#788157'
  // The same backdrop the board uses in game (MapDef.backdropColor, else the
  // board's default sea in MapView), so each preview carries its map's colour.
  const background = map?.backdropColor?.[dark ? 'dark' : 'light'] ?? (dark ? '#2c3520' : '#f4ecd3')
  const regions = React.useMemo(() => map
    ? assignRegionColors(map.regions, buildRegionAdjacency(map.territories, map.regions)) : {}, [map])
  const territoryRegions = React.useMemo(() => new Map(map?.regions.flatMap(region =>
    region.territoryIds.map(id => [id, region.id] as const)) ?? []), [map])
  const stroke = map ? Math.max(map.viewBox.width / width, map.viewBox.height / height) * 0.55 : 1
  return <View accessibilityRole="image" accessibilityLabel={`${map?.name ?? 'Selected board'} map preview`}
    style={{ width, height, backgroundColor: background, borderWidth: 1, borderColor: edge,
      borderRadius: 8, padding: 4, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' }}>
    {map ? <Svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet"
      viewBox={`${map.viewBox.x ?? 0} ${map.viewBox.y ?? 0} ${map.viewBox.width} ${map.viewBox.height}`}>
      {map.backgroundImage && MAP_BACKGROUND_IMAGES[map.backgroundImage] && <SvgImage
        x={map.viewBox.x ?? 0} y={map.viewBox.y ?? 0} width={map.viewBox.width} height={map.viewBox.height}
        href={MAP_BACKGROUND_IMAGES[map.backgroundImage]} preserveAspectRatio="none" pointerEvents="none" />}
      <MapDecorations decorations={map.decorations} />
      {!map.territoryMarkers && map.seaRoutes?.map(route => <Line key={`${route.aId}-${route.bId}`} x1={route.from.x} y1={route.from.y}
        x2={route.to.x} y2={route.to.y} stroke={edge} strokeWidth={stroke * 1.5} />)}
      {map.territories.map(territory => territory.shape ? <Path key={territory.id} d={territory.shape}
        fill={regions[territoryRegions.get(territory.id) ?? ''] ?? edge} fillOpacity={map.territoryMarkers ? 1 : dark ? 0.85 : 0.6}
        stroke={background} strokeWidth={stroke} /> : null)}
      {map.territoryMarkers && map.territories.map(territory => {
        const pos = territory.badgeAnchor ?? territory.position
        return <TerritoryMarker key={`marker-${territory.id}`} id={territory.id} x={pos.x} y={pos.y}
          radius={map.territoryMarkers!.radius} fill="#d7d8d8" />
      })}
      {map.wallsOverlay && MAP_BACKGROUND_IMAGES[map.wallsOverlay] && <SvgImage
        x={map.viewBox.x ?? 0} y={map.viewBox.y ?? 0} width={map.viewBox.width} height={map.viewBox.height}
        href={MAP_BACKGROUND_IMAGES[map.wallsOverlay]} preserveAspectRatio="none" pointerEvents="none" />}
    </Svg> : <Text className="font-typed text-ink-soft text-xs">Map</Text>}
  </View>
})
